import type { HydratedDocument } from 'mongoose';
import { z } from 'zod';
import { BufferSchema, DateSchema, type ObjectId, ObjectIdSchema } from './base';
import { EntityEventSchema } from './entityEvents';
import { SimCardNetworkSchema, SimCardSchema } from './simCard';
import type { UserDocument } from './user';

export enum TRACKER_TYPES {
  LK1062G = 'LK106-2G',
  LK1063G = 'LK106-3G',
  GL300 = 'GL300',
  GL300W = 'GL300W',
  GL300LTEME = 'GL300LTE-ME',
  GL300LTEMA = 'GL300LTE-MA',
  GL300LTEMG = 'GL300LTE-MG',
  GL310LTEMG = 'GL310LTE-MG',
  GL320LTEMG = 'GL320LTE-MG',
  GL30MG = 'GL30-MG',
  PrimeII = 'PrimeII',
  Mictrack = 'Mictrack',
  Teltonika = 'Teltonika',
  MT90 = 'MT90',
  M20 = 'M20',
  RRTrackbox = 'RRTrackbox',
  RRDecoder = 'RRDecoder',
  RRUbidium = 'RRUbidium',
  InsidersIntense = 'InsidersIntense',
  InsidersInspirit = 'InsidersInspirit',
  InsidersInfinity = 'InsidersInfinity',
  InsidersSimulator = 'InsidersSimulator',
  HttpGpsTracker = 'HttpGpsTracker',
}

export enum DeviceClasses {
  Ghost = 'Ghost',
  Upload = 'Upload',
  Mobile = 'Mobile',
  Manual = 'Manual',
  Tracker = 'Tracker',
  TrackBox = 'TrackBox',
  Integration = 'Integration',
  Transponder = 'Transponder',
  MyLapsTransponder = 'MyLapsTransponder',
  FeibotTransponder = 'FeibotTransponder',
  TrackPingTransponder = 'TrackPingTransponder',
  ChronoTrackTransponder = 'ChronoTrackTransponder',
  HttpGpsTracker = 'HttpGpsTracker',
}

export const DeviceClassTypes = {
  Ghost: {},
  Upload: {},
  Mobile: {},
  Manual: {},
  Tracker: {},
  TrackBox: { Passive: 'Passive', Active: 'Active', Decoder: 'Decoder' },
  Integration: {},
  Transponder: { Passive: 'Passive', Active: 'Active' },
  MyLapsTransponder: {},
  FeibotTransponder: {},
  TrackPingTransponder: { Passive: 'Passive', Active: 'Active' },
  ChronoTrackTransponder: {},
  HttpGpsTracker: {},
} as const;

export enum TrackerActivationType {
  NONE = 'NONE',
  ACTIVATED = 'ACTIVATED',
  RACEMAPTRACKER = 'RACEMAPTRACKER',
  ARCHIVED = 'ARCHIVED',
}

export enum LogisticsState {
  inStock = 'inStock',
  outOfStock = 'outOfStock',
}

export enum USER_ROLE {
  OWNER = 'owner',
  EDITOR = 'editor',
  BORROWER = 'borrower',
}

export enum RadioType {
  NONE = 'NONE',
  GSM = 'GSM',
  LTE = 'LTE',
  UMTS = 'UMTS',
}

export enum FIXED_REPORT_MODE {
  NONE = 'NONE',
  DISABLED = 'DISABLED',
  ENABLED = 'ENABLED',
  DISTANCE = 'DISTANCE',
  MILEAGE = 'MILEAGE',
  OPTIMUM = 'OPTIMUM',
  TIME_OR_MILEAGE = 'TIME_OR_MILEAGE',
}

export enum SERVER_CONNECTION_REPORT_MODE {
  UNKNOWN = 'UNKNOWN',
  DISABLED = 'DISABLED',
  TCP_SHORT = 'TCP_SHORT',
  TCP_SHORT_FORCED = 'TCP_SHORT_FORCED',
  TCP_LONG = 'TCP_LONG',
  UDP = 'UDP',
  SMS_FORCED = 'SMS_FORCED',
  UDP_FIXED = 'UDP_FIXED',
  TCP_LONG_BACKUP = 'TCP_LONG_BACKUP',
}

export enum BUFFER_MODE {
  UNKNOWN = 'UNKNOWN',
  DISABLED = 'DISABLED',
  ENABLED = 'ENABLED',
  HIGH_PRIORITY = 'HIGH_PRIORITY',
}

export enum NON_MOVE_REPORT_MODE {
  LAST_FIX = 'LAST_FIX',
  CURRENT_LOCATION = 'CURRENT_LOCATION',
  LAST_FIX_AND_CURRENT_LOCATION = 'LAST_FIX_AND_CURRENT_LOCATION',
}

export enum FUNCTION_KEY_MODE {
  NONE = 'NONE',
  DISABLED = 'DISABLED',
  GEO_FENCE_CURRENT_POSITION = 'GEO_FENCE_CURRENT_POSITION',
  GEO_FENCE = 'GEO_FENCE',
  SOS = 'SOS',
  LOCATION = 'LOCATION',
  MIXED = 'MIXED',
}

export enum POWER_KEY_MODE {
  NONE = 'NONE',
  NO_POWER_OFF = 'NO_POWER_OFF',
  POWER_OFF = 'POWER_OFF',
  SOS_MODE = 'SOS_MODE',
}

export enum CHARGING_BEHAVIOR_MODE {
  NONE = 'NONE',
  CHARGE_ONLY = 'CHARGE_ONLY',
  FULL_POWER_ON = 'FULL_POWER_ON',
  PURE_CHARGING = 'PURE_CHARGING',
}

export enum NETWORK_MODE {
  NONE = 'NONE',

  AUTO__CAT_M1_AND_CAT_NB2_M1_FIRST = 'AUTO__CAT_M1_AND_CAT_NB2_M1_FIRST',
  AUTO__CAT_M1_AND_CAT_NB2_NB2_FIRST = 'AUTO__CAT_M1_AND_CAT_NB2_NB2_FIRST',
  AUTO__CAT_M1 = 'AUTO__CAT_M1',
  AUTO__CAT_NB2 = 'AUTO__CAT_NB2',
  AUTO__CAT_NB2_FIRST = 'AUTO__CAT_NB2_FIRST',
  AUTO__CAT_NB2_ONLY = 'AUTO__CAT_NB2_ONLY',

  GSM_ONLY = 'GSM_ONLY',

  LTE_ONLY__CAT_M1_AND_CAT_NB2_M1_FIRST = 'LTE_ONLY__CAT_M1_AND_CAT_NB2_M1_FIRST',
  LTE_ONLY__CAT_M1_AND_CAT_NB2_NB2_FIRST = 'LTE_ONLY__CAT_M1_AND_CAT_NB2_NB2_FIRST',
  LTE_ONLY__CAT_M1 = 'LTE_ONLY__CAT_M1',
  LTE_ONLY__CAT_NB2 = 'LTE_ONLY__CAT_NB2',

  GSM_FIRST__CAT_M1_AND_CAT_NB2_M1_FIRST = 'GSM_FIRST__CAT_M1_AND_CAT_NB2_M1_FIRST',
  GSM_FIRST__CAT_M1_AND_CAT_NB2_NB2_FIRST = 'GSM_FIRST__CAT_M1_AND_CAT_NB2_NB2_FIRST',
  GSM_FIRST__CAT_M1 = 'GSM_FIRST__CAT_M1',
  GSM_FIRST__CAT_NB2 = 'GSM_FIRST__CAT_NB2',
}

export enum GPS_MODE {
  NONE = 'NONE',
  ON_NEED = 'ON_NEED',
  ALWAYS_ON = 'ALWAYS_ON',
  ON_DURING_MOVEMENT = 'ON_DURING_MOVEMENT',
}

export enum REPORT_TYPE {
  REST = 'REST',
  MOTION = 'MOTION',
  TURNING = 'TURNING',
}

export enum TRACKER_MESSAGE_TRANSPORT {
  SMS = 'sms',
  TCP = 'tcp',
}

export enum TRACKER_MESSAGE_HISTORY_RECORD_STATE {
  REVOKED = 'REVOKED',
  COMPLETE = 'COMPLETE',
  ERROR = 'ERROR',
  SENT = 'SENT',
  DELIVERED = 'DELIVERED',
  CLEARED = 'CLEARED',
}

export enum TRACKER_MESSAGE_HISTORY_RECORD_STATE_FRONTEND {
  PENDING = 'PENDING',
  PLANNED_FOR = 'PLANNED_FOR',
  CLOSE = 'CLOSE',
  NOT_SENT = 'NOT_SENT',
  PLANNED_AT = 'PLANNED_AT',
  NOT_ACKNOWLEDGED = 'NOT_ACKNOWLEDGED',
}

export type TRACKER_MESSAGE_HISTORY_RECORD_STATE_EXTENDED =
  | TRACKER_MESSAGE_HISTORY_RECORD_STATE
  | TRACKER_MESSAGE_HISTORY_RECORD_STATE_FRONTEND;

export enum TRACKER_PARTICIPANT_PAIRING_OPTION {
  GENERATE_NEW = 'GENERATE_NEW',
  PAIR_EXISTING = 'PAIR_EXISTING',
  PAIR_AND_GENERATE = 'PAIR_AND_GENERATE',
}

export enum DISCONNECT_REASON {
  NONE = 'NONE',
  SYNC_WITH_R_R = 'SYNC_WITH_R_R',
  WEBSOCKET_CLOSED = 'WEBSOCKET_CLOSED',
  SOCKET_CLOSED = 'SOCKET_CLOSED',
  SOCKET_ERROR = 'SOCKET_ERROR',
  SOCKET_TIMEOUT = 'SOCKET_TIMEOUT',
  SOCKET_ENDED = 'SOCKET_ENDED',
  POWER_OFF_BUFFERED = 'POWER_OFF_BUFFERED',
  POWER_OFF = 'POWER_OFF',
  KILL_SIGNAL = 'KILL_SIGNAL',
  NO_RECENT_LOCATION_UPDATE = 'NO_RECENT_LOCATION_UPDATE',
  MUTEX_TIMEOUT = 'MUTEX_TIMEOUT',
}

export type DeviceEvent =
  | '+RESP:GTPNA'
  | '+RESP:GTPFA'
  | '+RESP:GTEPN'
  | '+RESP:GTEPF'
  | '+RESP:GTBPL'
  | '+RESP:GTBTC'
  | '+RESP:GTSTC'
  | '+RESP:GTSTT'
  | '+RESP:GTPDP'
  | '+RESP:GTPNL'
  | '+RESP:GTIGN and +RESP:GTIGF'
  | '+RESP:GTIGL';

export type RRDeviceType =
  | TRACKER_TYPES.RRDecoder
  | TRACKER_TYPES.RRTrackbox
  | TRACKER_TYPES.RRUbidium;

export const TrackerSettingSchema = z.object({
  reportMode: z.enum(FIXED_REPORT_MODE).optional(),
  serverReportMode: z.enum(SERVER_CONNECTION_REPORT_MODE).optional(),
  bufferMode: z.enum(BUFFER_MODE).optional(),
  mainServerIp: z.string().nullish(),
  mainServerPort: z.number().nullish(),
  backupServerIp: z.string().nullish(),
  backupServerPort: z.number().nullish(),
  smsAckEnabled: z.boolean().nullish(),
  multiPacketSendingEnabled: z.boolean().nullish(),
  dnsLookupInterval: z.number().nullish(),
  moveCheckInterval: z.number().nullish(),
  moveSendInterval: z.number().nullish(),
  corner: z.number().nullish(),
  nonMoveBegin: z.number().nullish(),
  useRestIntervalsDuringNonMovement: z.boolean().nullish(),
  reportNonMovement: z.boolean().nullish(),
  reportMovement: z.boolean().nullish(),
  nonMoveCheckInterval: z.number().nullish(),
  nonMoveSendInterval: z.number().nullish(),
  nmdReportMode: z.string().nullish(),
  enterMovementByCmd: z.boolean().nullish(),
  estimatedTrackInterval: z.number().nullish(),
  gpsMode: z.enum(GPS_MODE).optional(),
  reportBeginTime: z.string().nullish(),
  reportEndTime: z.string().nullish(),
  reportDistance: z.number().nullish(),
  reportMileage: z.number().nullish(),
  heartbeatInterval: z.number().nullish(),
  heartbeatEnabled: z.boolean().nullish(),
  infoReportInterval: z.number().nullish(),
  infoReportEnabled: z.boolean().nullish(),
  serverAckEnabled: z.boolean().nullish(),
  functionKeyMode: z.enum(FUNCTION_KEY_MODE).optional(),
  chargingBehaviorMode: z.enum(CHARGING_BEHAVIOR_MODE).optional(),
  powerKeyMode: z.enum(POWER_KEY_MODE).optional(),
  networkMode: z.enum(NETWORK_MODE).optional(),
  allowedNetworkOperators: z.array(z.string()).nullish(),
  blockedNetworkOperators: z.array(z.string()).nullish(),
  activatedDeviceEvents: z.array(z.string()).nullish(),
  isMotionSensorEnabled: z.boolean().nullish(),
  motionSensorSensitivity: z.number().nullish(),
});

export type TrackerSetting = z.infer<typeof TrackerSettingSchema>;

export const TrackerStateSchema = z.object({
  // optional because of archived state
  lastConnectedAt: DateSchema.optional(),
  lastDisconnectedAt: DateSchema.optional(),
  disconnectReason: z.enum(DISCONNECT_REASON).default(DISCONNECT_REASON.NONE).optional(),
  lastDataAt: DateSchema.optional(),
  movement: z.boolean().optional(),
  battery: z.number().optional(),
  batteryVoltage: z.number().optional(),
  charging: z.boolean().optional(),
  gpsActiv: z.boolean().optional(),
  temperature: z.number().optional(),
  gpsFix: z.boolean().optional(),
  registered: z.boolean().optional(),
  signalStrength: z.number().optional(),
  isDataPossible: z.boolean().optional(),
  heartbeatLastAt: z.date().optional(),
  infoReportLastAt: z.date().optional(),
});
export type TrackerState = z.infer<typeof TrackerStateSchema>;

export const TrackerActivationSchema = z.object({
  type: z.enum(TrackerActivationType),
  paidUntil: DateSchema.nullish(),
});

export type TrackerActivation = z.infer<typeof TrackerActivationSchema>;

export const TrackerSatellitesSchema = z.object({
  satelliteNumber: z.number().default(-1),
  satelliteList: z
    .array(z.object({ satelliteId: z.number(), satellitePower: z.number() }))
    .default([]),
});

export type TrackerSatellites = z.infer<typeof TrackerSatellitesSchema>;

export const BorrowerSchema = z.object({
  id: ObjectIdSchema,
  startTime: DateSchema,
  endTime: DateSchema,
});
export type Borrower = z.infer<typeof BorrowerSchema>;

export const LastGeoSchema = z.object({
  // optional because of archived state
  lng: z.number().optional(),
  lat: z.number().optional(),
  lastTime: DateSchema.optional(),
  elv: z.number().optional(),
  accuracy: z.number().optional(),
  speed: z.number().optional(),
  azimuth: z.number().optional(),
  reportType: z.enum(REPORT_TYPE).nullable().default(null).optional(),
});
export type LastGeo = z.infer<typeof LastGeoSchema>;

export const TrackerPrototypeSchema = z.object({
  id: ObjectIdSchema.optional(),
  trackerId: z.string(),
  trackerType: z.enum(TRACKER_TYPES),
  trackerName: z.string(),
  iccid: z.string().optional(),
});
export type TrackerPrototype = z.infer<typeof TrackerPrototypeSchema>;

export const TrackerMetaSchema = z.object({
  firmwareTag: z.string().optional(),
  firmwareHash: z.string().optional(),
  firmwareBranch: z.string().optional(),
  firmwareBuildDate: DateSchema.optional(),
  firmwareVersion: z.string().optional(),
  hardwareVersion: z.string().optional(),
  hardwareModem: z.string().optional(),
  hardwareModemSW: z.string().optional(),
  bootloaderVersion: z.string().optional(),
  simId: z.string().optional(),
  iccid: z.string().optional(),
});
export type TrackerMeta = z.infer<typeof TrackerMetaSchema>;

export const TrackerLogisticsSchema = z.enum(LogisticsState);
export type TrackerLogistics = z.infer<typeof TrackerLogisticsSchema>;

const CellSchema = z.object({
  lac: z.number(),
  cid: z.number(),
  rxlev: z.number().optional(),
});

export type Cell = z.infer<typeof CellSchema>;

export const TrackerNetworkSchema = z.object({
  mcc: z.string().optional(),
  mnc: z.string().optional(),
  cellId: z.string().optional(),
  lac: z.string().optional(),
  name: z.string().optional(),
  country: z.string().optional(),
  countryCode: z.string().optional(),
  radio: z.enum(RadioType).optional(),
  cellLocation: z
    .object({
      accuracy: z.number().optional(),
      lat: z.number().optional(),
      lng: z.number().optional(),
    })
    .optional(),
  updatedAt: DateSchema.optional(),
});
export type TrackerNetwork = z.infer<typeof TrackerNetworkSchema>;

export const TrackerSchema = TrackerPrototypeSchema.extend({
  id: ObjectIdSchema,
  state: TrackerStateSchema,
  lastGeo: LastGeoSchema,
  createdAt: DateSchema,
  updatedAt: DateSchema,
  activation: TrackerActivationSchema,
  editors: z.array(ObjectIdSchema),
  owner: ObjectIdSchema,
  borrowers: z.array(BorrowerSchema),
  notes: z.string(),
  messageCounter: z.number().default(0),
  // optional because of archived state
  logistics: TrackerLogisticsSchema.optional(),
  setting: TrackerSettingSchema.optional(),
  satellites: TrackerSatellitesSchema.optional(),
  network: TrackerNetworkSchema.optional(),
  rfidUid: z.string().min(1).optional(),
  password: z.string().optional(),
  meta: TrackerMetaSchema.optional(),
  createdByTest: z.boolean().default(false),
});
export type Tracker = z.infer<typeof TrackerSchema>;

export const PublicTrackerSchema = TrackerPrototypeSchema.pick({ trackerName: true }).extend({
  id: ObjectIdSchema,
  activation: TrackerActivationSchema,
  state: TrackerStateSchema,
  lastGeo: LastGeoSchema.pick({ lat: true, lng: true, elv: true, lastTime: true }),
  updatedAt: DateSchema,
});
export type PublicTracker = z.infer<typeof PublicTrackerSchema>;

export const TrackerMessageHistoryRecordSchema = z.object({
  id: ObjectIdSchema,
  statusCode: z.enum(TRACKER_MESSAGE_HISTORY_RECORD_STATE),
  creatorId: ObjectIdSchema.nullable(),
  createdAt: DateSchema,
  updatedAt: DateSchema,
  messageId: ObjectIdSchema,
  statusMessage: z.string().nullable(),
  payload: BufferSchema.optional(),
  createdByTest: z.boolean().optional(),
});

export const TrackerMessageHistoryRecordDocumentSchema = TrackerMessageHistoryRecordSchema.extend({
  updatedAt: z.date(),
  createdAt: z.date(),
  createdByTest: z.boolean().optional(),
  _id: ObjectIdSchema,
});

export type TrackerMessageHistoryRecordDocument = Omit<
  HydratedDocument<TrackerMessageHistoryRecord, TrackerMessageDocumentMethods>,
  'id'
>;

export type TrackerMessageHistoryRecord = z.infer<typeof TrackerMessageHistoryRecordSchema>;

export const TrackerMessageSchema = z.object({
  id: ObjectIdSchema,
  payload: BufferSchema,
  templateId: ObjectIdSchema.nullable(),
  parameters: z.record(z.string(), z.string()).optional(),
  label: z.string(),
  createdAt: DateSchema,
  updatedAt: DateSchema,
  plannedAt: DateSchema,
  closeAfter: DateSchema,
  maxNumOfAttempts: z.number().default(3),
  trackerId: ObjectIdSchema,
  creatorId: ObjectIdSchema,
  responseId: z.string().nullable(),
  transport: z.enum(TRACKER_MESSAGE_TRANSPORT).default(TRACKER_MESSAGE_TRANSPORT.TCP),
  historyRecords: z.array(TrackerMessageHistoryRecordSchema).default([]),
  shouldAck: z.boolean(),
  events: z.array(EntityEventSchema).default([]),
});
export type TrackerMessage = z.infer<typeof TrackerMessageSchema>;

export const TrackerMessagesSchema = z.array(TrackerMessageSchema);
export type TrackerMessages = z.infer<typeof TrackerMessagesSchema>;

export const TrackerMessageOverviewSchema = TrackerMessageSchema.omit({ payload: true });
export type TrackerMessageOverview = z.infer<typeof TrackerMessageOverviewSchema>;

export const TrackerMessagePrototypeSchema = TrackerMessageSchema.partial().required({
  payload: true,
  trackerId: true,
  plannedAt: true,
});
export type TrackerMessagePrototype = z.infer<typeof TrackerMessagePrototypeSchema>;

export const TrackerMessageTemplateBasedPrototypeSchema = TrackerMessagePrototypeSchema.partial()
  .required({
    templateId: true,
    parameters: true,
  })
  .omit({
    payload: true,
    responseId: true,
    trackerId: true,
    creatorId: true,
    createdAt: true,
    updatedAt: true,
    id: true,
  });
export type TrackerMessageTemplateBasedPrototype = z.infer<
  typeof TrackerMessageTemplateBasedPrototypeSchema
>;

export const TrackerMessageHistoryRecordPrototypeSchema =
  TrackerMessageHistoryRecordSchema.partial().required({
    statusCode: true,
    messageId: true,
  });
export type TrackerMessageHistoryPrototype = z.infer<
  typeof TrackerMessageHistoryRecordPrototypeSchema
>;

export const SimCardOverviewSchema = z.object({
  id: ObjectIdSchema,
  iccid: z.string(),
  userId: ObjectIdSchema.nullable(),
  status: SimCardSchema.shape.status,
  lastConnectionAt: SimCardSchema.shape.lastConnectionAt.optional(),
  lastDisconnectionAt: SimCardSchema.shape.lastDisconnectionAt.optional(),
  lastMcc: SimCardNetworkSchema.shape.mcc.nullish(),
  lastMnc: SimCardNetworkSchema.shape.mnc.nullish(),
});

export type SimCardOverview = z.infer<typeof SimCardOverviewSchema>;

export const MessageCountsSchema = z.object({
  errorCount: z.number(),
  pendingCount: z.number(),
  scheduledCount: z.number(),
});
export type MessageCounts = z.infer<typeof MessageCountsSchema>;

export const EnrichedTrackerSchema = TrackerSchema.extend({
  messagesCounts: MessageCountsSchema,
  lastMessageAt: DateSchema.nullish(),
  eventIds: z.array(ObjectIdSchema),
  simCard: SimCardOverviewSchema.nullable(),
});

export type EnrichedTracker = z.infer<typeof EnrichedTrackerSchema>;

export interface TrackerMessageDocumentMethods {
  canEdit(user: UserDocument | Express.User | null | undefined): Promise<boolean>;
  canRead(user: UserDocument | Express.User | null | undefined): Promise<boolean>;
  createMessageHistoryRecord(
    creatorId: ObjectId | null,
    trackerMessageState: TRACKER_MESSAGE_HISTORY_RECORD_STATE,
    statusMessage?: string | null,
    createByTest?: boolean,
  ): Promise<TrackerMessageHistoryRecord>;
}

export type TrackerMessageDocument = Omit<
  HydratedDocument<TrackerMessage, TrackerMessageDocumentMethods>,
  'id'
>;

export type TrackerMessageError = z.infer<typeof TrackerMessageErrorSchema>;

export const TrackerMessageErrorSchema = z.object({
  message: z.string(),
  trackerId: ObjectIdSchema,
  templateId: ObjectIdSchema,
});

export const AddTrackerMessagesResponseSchema = z.object({
  success: z.boolean(),
  messages: z.array(TrackerMessageSchema),
  errors: z.array(TrackerMessageErrorSchema),
});

export type AddTrackerMessagesResponse = z.infer<typeof AddTrackerMessagesResponseSchema>;

export const AddTrackerMessagesRequestBodySchema =
  TrackerMessageTemplateBasedPrototypeSchema.extend({
    trackerIds: z.array(ObjectIdSchema),
  });
export type AddTrackerMessagesRequestBody = z.infer<typeof AddTrackerMessagesRequestBodySchema>;
